/**
 * Field Calculator's expression engine.
 *
 * Esri's own Field Calculator offers a choice of expression language — SQL
 * or Python (Arcade, in newer ArcGIS, is a third). This offers the same
 * CHOICE for familiarity, but underneath is one small, safe evaluator, not
 * two real interpreters — there is no SQL engine and no Python runtime in a
 * browser to call out to, and `eval`/`new Function` on a value typed by
 * whoever opened this dialog is a code-injection hole, not a shortcut. What
 * actually differs between the two tabs is only the FIELD-REFERENCE syntax
 * (`[Field]` for SQL, `!Field!` for Python, matching each product's own
 * convention) and which keyword spells "and"/"or"/"not" — every operator,
 * function and literal works identically either way.
 *
 * A hand-written recursive-descent parser, not a library: the whole grammar
 * is small (arithmetic, comparisons, a handful of functions, one field
 * reference), and a parser this size is easier to read and to keep safe than
 * pulling in a general expression-language dependency for it.
 */

export type ExprValue = number | string | boolean | null;

type Token =
  | { kind: 'number'; value: number }
  | { kind: 'string'; value: string }
  | { kind: 'field'; name: string }
  | { kind: 'ident'; name: string }
  | { kind: 'op'; value: string }
  | { kind: 'eof' };

type Node =
  | { kind: 'number'; value: number }
  | { kind: 'string'; value: string }
  | { kind: 'bool'; value: boolean }
  | { kind: 'null' }
  | { kind: 'field'; name: string }
  | { kind: 'unary'; op: '-' | 'not'; operand: Node }
  | { kind: 'binary'; op: string; left: Node; right: Node }
  | { kind: 'ternary'; condition: Node; whenTrue: Node; whenFalse: Node }
  | { kind: 'call'; name: string; args: Node[] };

const KEYWORDS = new Set(['and', 'or', 'not', 'true', 'false', 'null']);

/** Multi-character operators, longest first so `>=` isn't read as `>` then `=`. */
const OPERATORS = ['<>', '>=', '<=', '==', '!=', '=', '>', '<', '+', '-', '*', '/', '%', '(', ')', ',', '?', ':'];

function tokenize(source: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  const n = source.length;

  while (i < n) {
    const ch = source[i];

    if (/\s/.test(ch)) {
      i += 1;
      continue;
    }

    // [FieldName] — SQL-style reference. Anything up to the closing bracket
    // is the field's own name, so a field called "Pipe Length" never needs
    // its own quoting rule.
    if (ch === '[') {
      const end = source.indexOf(']', i + 1);
      if (end === -1) throw new Error('Missing closing "]" after "["');
      tokens.push({ kind: 'field', name: source.slice(i + 1, end) });
      i = end + 1;
      continue;
    }

    // !FieldName! — Python-style reference, Esri's own convention for its
    // Python field calculator. Distinguished from nothing else: this
    // language has no "!" operator of its own (negation is spelled NOT), so
    // every "!" the tokenizer sees opens a field reference.
    if (ch === '!') {
      const end = source.indexOf('!', i + 1);
      if (end === -1) throw new Error('Missing closing "!" after "!"');
      tokens.push({ kind: 'field', name: source.slice(i + 1, end) });
      i = end + 1;
      continue;
    }

    if (ch === '"' || ch === "'") {
      const quote = ch;
      let value = '';
      i += 1;
      while (i < n && source[i] !== quote) {
        // A doubled quote ('' or "") is the one escape this supports — the
        // same convention SQL string literals already use.
        if (source[i] === quote && source[i + 1] === quote) {
          value += quote;
          i += 2;
          continue;
        }
        value += source[i];
        i += 1;
      }
      if (i >= n) throw new Error(`Missing closing ${quote} in string literal`);
      i += 1;
      tokens.push({ kind: 'string', value });
      continue;
    }

    if (/[0-9]/.test(ch) || (ch === '.' && /[0-9]/.test(source[i + 1] ?? ''))) {
      let j = i;
      while (j < n && /[0-9.]/.test(source[j])) j += 1;
      const text = source.slice(i, j);
      const value = Number(text);
      if (Number.isNaN(value)) throw new Error(`"${text}" is not a valid number`);
      tokens.push({ kind: 'number', value });
      i = j;
      continue;
    }

    if (/[A-Za-z_]/.test(ch)) {
      let j = i;
      while (j < n && /[A-Za-z0-9_]/.test(source[j])) j += 1;
      const word = source.slice(i, j);
      const lower = word.toLowerCase();
      if (KEYWORDS.has(lower)) {
        tokens.push({ kind: 'op', value: lower });
      } else {
        // A bare word is either a function name (followed by "(") or a
        // field name — the parser decides which once it sees what follows;
        // the tokenizer just hands both the same token kind.
        tokens.push({ kind: 'ident', name: word });
      }
      i = j;
      continue;
    }

    const two = source.slice(i, i + 2);
    if (OPERATORS.includes(two)) {
      tokens.push({ kind: 'op', value: two });
      i += 2;
      continue;
    }
    const one = source.slice(i, i + 1);
    if (OPERATORS.includes(one)) {
      tokens.push({ kind: 'op', value: one });
      i += 1;
      continue;
    }

    throw new Error(`Unexpected character "${ch}"`);
  }

  tokens.push({ kind: 'eof' });
  return tokens;
}

/**
 * Recursive-descent parser. One method per precedence level, lowest
 * (ternary) to highest (a literal or a parenthesised group) — the standard
 * shape for a hand-written expression parser, so operator precedence is
 * exactly the levels below and nothing has to consult a precedence table.
 */
class Parser {
  private pos = 0;
  constructor(private tokens: Token[]) {}

  private peek(): Token {
    return this.tokens[this.pos];
  }
  private next(): Token {
    return this.tokens[this.pos++];
  }
  private isOp(value: string): boolean {
    const token = this.peek();
    return token.kind === 'op' && token.value === value;
  }
  private expectOp(value: string) {
    if (!this.isOp(value)) {
      const token = this.peek();
      const found = token.kind === 'op' ? token.value : token.kind === 'eof' ? 'end of expression' : token.kind;
      throw new Error(`Expected "${value}" but found "${found}"`);
    }
    this.next();
  }

  parse(): Node {
    const node = this.ternary();
    if (this.peek().kind !== 'eof') {
      const token = this.peek();
      throw new Error(`Unexpected "${token.kind === 'op' ? token.value : token.kind}" after the expression`);
    }
    return node;
  }

  private ternary(): Node {
    const condition = this.or();
    if (this.isOp('?')) {
      this.next();
      const whenTrue = this.ternary();
      this.expectOp(':');
      const whenFalse = this.ternary();
      return { kind: 'ternary', condition, whenTrue, whenFalse };
    }
    return condition;
  }

  private or(): Node {
    let left = this.and();
    while (this.isOp('or')) {
      this.next();
      left = { kind: 'binary', op: 'or', left, right: this.and() };
    }
    return left;
  }

  private and(): Node {
    let left = this.not();
    while (this.isOp('and')) {
      this.next();
      left = { kind: 'binary', op: 'and', left, right: this.not() };
    }
    return left;
  }

  private not(): Node {
    if (this.isOp('not')) {
      this.next();
      return { kind: 'unary', op: 'not', operand: this.not() };
    }
    return this.comparison();
  }

  private comparison(): Node {
    const left = this.additive();
    const comparators = ['==', '=', '!=', '<>', '>=', '<=', '>', '<'];
    const token = this.peek();
    if (token.kind === 'op' && comparators.includes(token.value)) {
      this.next();
      const op = token.value === '=' ? '==' : token.value === '<>' ? '!=' : token.value;
      return { kind: 'binary', op, left, right: this.additive() };
    }
    return left;
  }

  private additive(): Node {
    let left = this.multiplicative();
    while (this.isOp('+') || this.isOp('-')) {
      const op = this.next() as { kind: 'op'; value: string };
      left = { kind: 'binary', op: op.value, left, right: this.multiplicative() };
    }
    return left;
  }

  private multiplicative(): Node {
    let left = this.unary();
    while (this.isOp('*') || this.isOp('/') || this.isOp('%')) {
      const op = this.next() as { kind: 'op'; value: string };
      left = { kind: 'binary', op: op.value, left, right: this.unary() };
    }
    return left;
  }

  private unary(): Node {
    if (this.isOp('-')) {
      this.next();
      return { kind: 'unary', op: '-', operand: this.unary() };
    }
    return this.primary();
  }

  private primary(): Node {
    const token = this.next();

    if (token.kind === 'number') return { kind: 'number', value: token.value };
    if (token.kind === 'string') return { kind: 'string', value: token.value };
    if (token.kind === 'field') return { kind: 'field', name: token.name };

    if (token.kind === 'op' && token.value === 'true') return { kind: 'bool', value: true };
    if (token.kind === 'op' && token.value === 'false') return { kind: 'bool', value: false };
    if (token.kind === 'op' && token.value === 'null') return { kind: 'null' };

    if (token.kind === 'op' && token.value === '(') {
      const inner = this.ternary();
      this.expectOp(')');
      return inner;
    }

    if (token.kind === 'ident') {
      if (this.isOp('(')) {
        this.next();
        const args: Node[] = [];
        if (!this.isOp(')')) {
          args.push(this.ternary());
          while (this.isOp(',')) {
            this.next();
            args.push(this.ternary());
          }
        }
        this.expectOp(')');
        return { kind: 'call', name: token.name.toLowerCase(), args };
      }
      // A bare word that is not a function call is a field reference —
      // the same convention a plain column name has in real SQL, so
      // `LENGTH_M * 3.28084` works without bracketing every field.
      return { kind: 'field', name: token.name };
    }

    throw new Error(
      token.kind === 'eof' ? 'The expression ended unexpectedly' : `Unexpected "${(token as any).value}"`,
    );
  }
}

function toNumber(value: ExprValue): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (value === null || value === undefined || value === '') return NaN;
  const parsed = Number(value);
  return parsed;
}

function toStr(value: ExprValue): string {
  if (value === null || value === undefined) return '';
  return String(value);
}

function truthy(value: ExprValue): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0 && !Number.isNaN(value);
  if (value === null || value === undefined) return false;
  return String(value).trim() !== '';
}

const FUNCTIONS: Record<string, (args: ExprValue[]) => ExprValue> = {
  upper: (a) => toStr(a[0]).toUpperCase(),
  lower: (a) => toStr(a[0]).toLowerCase(),
  trim: (a) => toStr(a[0]).trim(),
  len: (a) => toStr(a[0]).length,
  length: (a) => toStr(a[0]).length,
  concat: (a) => a.map(toStr).join(''),
  substr: (a) => {
    const text = toStr(a[0]);
    const start = Math.max(0, Math.trunc(toNumber(a[1])));
    const len = a.length > 2 ? Math.max(0, Math.trunc(toNumber(a[2]))) : undefined;
    return len === undefined ? text.slice(start) : text.slice(start, start + len);
  },
  round: (a) => {
    const digits = a.length > 1 ? Math.trunc(toNumber(a[1])) : 0;
    const factor = 10 ** digits;
    return Math.round(toNumber(a[0]) * factor) / factor;
  },
  abs: (a) => Math.abs(toNumber(a[0])),
  floor: (a) => Math.floor(toNumber(a[0])),
  ceil: (a) => Math.ceil(toNumber(a[0])),
  sqrt: (a) => Math.sqrt(toNumber(a[0])),
  // IIF(condition, whenTrue, whenFalse) — SQL Server's and Esri Arcade's own
  // name for `condition ? a : b`; offered alongside the ?: syntax since
  // whoever is typing a SQL-flavoured expression likely already knows it.
  iif: (a) => (truthy(a[0]) ? a[1] : a[2]),
  coalesce: (a) => a.find((value) => value !== null && value !== undefined && value !== '') ?? null,
};

function evaluate(node: Node, properties: Record<string, unknown>): ExprValue {
  switch (node.kind) {
    case 'number':
      return node.value;
    case 'string':
      return node.value;
    case 'bool':
      return node.value;
    case 'null':
      return null;
    case 'field': {
      const value = properties[node.name];
      if (value === undefined) return null;
      if (typeof value === 'number' || typeof value === 'string' || typeof value === 'boolean') return value;
      return value === null ? null : String(value);
    }
    case 'unary': {
      const operand = evaluate(node.operand, properties);
      return node.op === '-' ? -toNumber(operand) : !truthy(operand);
    }
    case 'ternary':
      return truthy(evaluate(node.condition, properties))
        ? evaluate(node.whenTrue, properties)
        : evaluate(node.whenFalse, properties);
    case 'call': {
      const fn = FUNCTIONS[node.name];
      if (!fn) {
        const known = Object.keys(FUNCTIONS).join(', ');
        throw new Error(`Unknown function "${node.name.toUpperCase()}" — known functions: ${known}`);
      }
      return fn(node.args.map((arg) => evaluate(arg, properties)));
    }
    case 'binary': {
      const { op } = node;
      if (op === 'and') return truthy(evaluate(node.left, properties)) && truthy(evaluate(node.right, properties));
      if (op === 'or') return truthy(evaluate(node.left, properties)) || truthy(evaluate(node.right, properties));

      const left = evaluate(node.left, properties);
      const right = evaluate(node.right, properties);

      if (op === '+') {
        // Numeric when both sides genuinely are; string concatenation
        // otherwise — the same forgiving rule both SQL's `+` (numeric-only,
        // worked around with CONCAT/`||` in real SQL) and Python's `+`
        // (works on either, but refuses to MIX them) exist to avoid typing
        // two different operators for "join these" depending on type.
        const bothNumeric = typeof left === 'number' && typeof right === 'number';
        return bothNumeric ? left + right : toStr(left) + toStr(right);
      }
      if (op === '-') return toNumber(left) - toNumber(right);
      if (op === '*') return toNumber(left) * toNumber(right);
      if (op === '/') return toNumber(left) / toNumber(right);
      if (op === '%') return toNumber(left) % toNumber(right);

      if (op === '==' || op === '!=') {
        const equal =
          typeof left === 'number' || typeof right === 'number'
            ? toNumber(left) === toNumber(right)
            : toStr(left) === toStr(right);
        return op === '==' ? equal : !equal;
      }
      // Ordering comparisons: numeric when both sides look numeric, string
      // comparison otherwise — the same split calc_field logic above uses,
      // so `[DATE] > '2024-01-01'` reads correctly while `[LENGTH] > 100`
      // stays a real numeric comparison rather than lexical ("9" > "100").
      const numericCompare = !Number.isNaN(toNumber(left)) && !Number.isNaN(toNumber(right));
      const a = numericCompare ? toNumber(left) : toStr(left);
      const b = numericCompare ? toNumber(right) : toStr(right);
      if (op === '>') return a > b;
      if (op === '>=') return a >= b;
      if (op === '<') return a < b;
      if (op === '<=') return a <= b;
      throw new Error(`Unknown operator "${op}"`);
    }
    default:
      throw new Error('Unreachable expression node');
  }
}

export interface CompiledExpression {
  run: (properties: Record<string, unknown>) => ExprValue;
}

/** Parses once; throws a message meant to be shown directly in the dialog,
 *  not a raw JS error, since there is no stack trace a non-technical reader
 *  could use anyway. */
export function compileExpression(source: string): CompiledExpression {
  const trimmed = source.trim();
  if (!trimmed) throw new Error('Enter an expression.');
  const ast = new Parser(tokenize(trimmed)).parse();
  return {
    run: (properties: Record<string, unknown>) => evaluate(ast, properties),
  };
}

/** The function names this language knows, for a hint under the expression box. */
export const FIELD_CALCULATOR_FUNCTIONS = Object.keys(FUNCTIONS)
  .map((name) => name.toUpperCase())
  .sort();
